// Cloud login via Keycloak Authorization Code + PKCE (the official keycloak-js adapter). The login UI
// is Keycloak's hosted page (branded by the `fla` theme), so first-login password changes, temporary
// passwords, and any required actions work — things ROPC could not do (see ADR-0007). Only used when
// the server advertises authRequired; standalone/offline never imports/touches Keycloak.

import Keycloak from 'keycloak-js';

export interface OidcConfig {
  issuer: string; // e.g. https://auth.wolpold.com/realms/fla
  realm: string;
  clientId: string;
}

let kc: Keycloak | null = null;

/** Init keycloak-js with login-required: if not signed in, this redirects to the hosted login and the
 *  promise never resolves in-page (the browser navigates away); on return it resolves authenticated. */
export async function initAuth(oidc: OidcConfig): Promise<boolean> {
  const url = oidc.issuer.replace(/\/realms\/[^/]+\/?$/, ''); // strip /realms/<realm> → server base
  kc = new Keycloak({ url, realm: oidc.realm, clientId: oidc.clientId });
  const authenticated = await kc.init({
    onLoad: 'login-required',
    pkceMethod: 'S256',
    checkLoginIframe: false,
    redirectUri: `${window.location.origin}/`,
  });
  kc.onTokenExpired = () => void kc?.updateToken(30).catch(() => kc?.login());
  return authenticated;
}

/** Refresh the access token if it expires within 30s (no-op otherwise). */
export async function updateToken(): Promise<void> {
  try {
    await kc?.updateToken(30);
  } catch {
    /* refresh failed — the next 401 / WS 1008 path will re-login */
  }
}

export const token = (): string | undefined => kc?.token;
export const authHeader = (): Record<string, string> =>
  kc?.token ? { authorization: `Bearer ${kc.token}` } : {};
export function logout(): void {
  void kc?.logout({ redirectUri: `${window.location.origin}/` });
}
